import React from "react";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "helpers/reducers";
import clsx from "clsx";
import Dates from "utils/Dates";
import {currencyNumberFormat, currencyNumberFormatNoDigit} from "utils/utility";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {getDurationOptions, usePackageItemKartingOptions} from "utils/packageUtils";
import {useHallOpeningHours} from "utils/hooks/useHallOpeningHours";
import {PackagesItemsPerson} from "types/entities/PackagesItems";
import {PackageCreditItemType} from "types/enums/PackageCreditItemType";
import {DEFAULT_STANDARD_CREDIT_PACKAGE_AMOUNT} from "helpers/config";
import {pricePerCredits} from "utils/creditsUtils";

interface BookingPackagesCreditsHeaderStandardProps {
    className?: string;
}

const TopRecommendation = () => {
    const {t} = useTranslation();

    return (
        <div
            className={
                "w-[102px] lg:w-[120px] rounded-t-xl flex flex-row lg:flex-row items-center justify-center gap-1.5 px-1 py-1.5 font-semibold leading-none text-xxs bg-[linear-gradient(90deg,#FFAE00_0%,#E1570B_35%,#D12C11_70%,#C20016_100%)] text-white"
            }
        >
            <div className={"flex flex-row items-center gap-1"}>
                <img src="/img/credits/top-recommendation-icon.svg" alt="top recemmendation" className="size-[12px]" />
                <span>
                    {t("Top")} {t("recommendation")}
                </span>
            </div>
        </div>
    );
};

const BookingPackagesCreditsHeaderStandard = (props: BookingPackagesCreditsHeaderStandardProps) => {
    const {t} = useTranslation();
    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const availableCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.availableCreditItems);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const {getPackageItemForCombination, getPackageItemPrice} = usePackageItemKartingOptions();
    const hallOpeningHours = useHallOpeningHours();
    const isDisable1Hour: boolean = Dates.hour(selectedTimeslot?.start_at) < 17;
    const isDisabled2Hours: boolean = false;
    const durationOptions = getDurationOptions(isDisabled2Hours, isDisable1Hour);
    const creditItems = availableCreditItems.filter(item => item?.article?.creditPackage?.category === PackageCreditItemType.Standard);
    const sortedPackageCreditItems = [...creditItems].sort((a, b) => a.article.article_value - b.article.article_value);

    const isPackageItemDisabledForCombination = duration => {
        // if (!props.enabled) {
        //     return true;
        // }
        // const packagesItemsPerson: PackagesItemsPerson | undefined = getPackageItemForCombination(duration, false, props.personType);
        const packagesItemsPerson: PackagesItemsPerson | undefined = getPackageItemForCombination(duration, false, PackageItemPersonType.Adult);
        return packagesItemsPerson === undefined;
    };

    const getFirstWord = (value: string): string => {
        return value.trim().split(/\s+/)[0] ?? "";
    };

    return (
        <div
            className={clsx(
                "BookingPackagesCreditsHeaderStandard hidden lg:grid grid-cols-[10fr_37fr_44fr_9fr] xl:grid-cols-[10fr_36fr_42fr_12fr] w-full gap-4 px-2",
                props?.className
            )}
        >
            <div></div>

            {/* tickets */}
            <div className={"relative w-full items-center flex flex-row justify-start lg:justify-center"}>
                <div className={"flex flex-col items-end space-y-2 sm:space-y-0 w-full sm:w-auto sm:grid sm:grid-cols-3 gap-2 lg:gap-1 xl:gap-2"}>
                    {durationOptions.map(option => {
                        const isDisabledFromPackageItem = isPackageItemDisabledForCombination(option.value);
                        const isRecommendationOption = option.value === 270 || option.value === 240;

                        if (isDisabledFromPackageItem) {
                            return;
                        }

                        return (
                            <div key={option.value} className={"flex flex-col"}>
                                {isRecommendationOption && <TopRecommendation />}

                                <div
                                    className={clsx(
                                        "w-[102px] lg:w-[120px] h-[80px] relative flex flex-col items-center justify-center transition-all duration-150 px-1 xl:px-2 py-1.5",
                                        "bg-[#FAF7EE] text-black",
                                        isRecommendationOption ? "rounded-b-xl" : "rounded-xl"
                                    )}
                                >
                                    <span className={clsx("font-bold text-base whitespace-nowrap mb-1")}>{option.name}</span>
                                    <span className={"font-semibold text-sm"}>
                                        {currencyNumberFormatNoDigit(getPackageItemPrice(option.value, PackageItemPersonType.Adult))}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* credits */}
            <div className={"relative w-full items-center flex flex-row justify-start lg:justify-center"}>
                <div className={"flex flex-row items-end justify-center space-y-2 sm:space-y-0 w-full gap-2 lg:gap-1 xl:gap-2"}>
                    <div className={"flex flex-col"}>
                        <div
                            className={clsx(
                                "w-[64px] h-[80px] relative flex flex-col items-center justify-center rounded-xl transition-all duration-150  border-2 border-dashed px-1 py-1.5 border-[#FBFBFB] bg-white text-gray-800"
                            )}
                        >
                            <div className={"w-full flex flex-col items-center justify-center transition-colors duration-150"}>
                                {/*<span className={"font-bold text-base"}>{t("None")}</span>*/}
                                <span className={"font-bold text-base whitespace-nowrap mb-1 tracking-normal"}>{t("Keine")}</span>
                                <span className={"font-semibold text-sm"}>{currencyNumberFormatNoDigit(0)}</span>
                            </div>
                        </div>
                    </div>

                    {sortedPackageCreditItems.map(item => {
                        const isRecommendationOption = item.article.creditPackage?.amount_total === DEFAULT_STANDARD_CREDIT_PACKAGE_AMOUNT;
                        return (
                            <div key={item.id} className={"flex flex-col"}>
                                {isRecommendationOption && <TopRecommendation />}
                                <div
                                    className={clsx(
                                        "w-[102px] lg:w-[120px] h-[80px] relative flex flex-col items-center justify-end transition-all duration-150 px-1 xl:px-2 py-1.5",
                                        "bg-light-grey text-black",
                                        isRecommendationOption ? "rounded-b-xl" : "rounded-xl"
                                    )}
                                >
                                    <span className={"font-bold text-base whitespace-nowrap leading-tight"}>
                                        {item.article?.creditPackage?.name ?? t("Unknown")}
                                    </span>
                                    <span className={"font-semibold text-sm mb-2"}>{currencyNumberFormat(item.article?.article_value)}</span>
                                    <span className={"font-medium text-xxs"}>
                                        {currencyNumberFormat(pricePerCredits(item.article?.creditPackage?.amount_total ?? 0))} {t("per credit")}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div></div>
        </div>
    );
};

export default BookingPackagesCreditsHeaderStandard;
